import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './PurchaseOrder.css';
import axiosInstance from '../../../Config/axiosInstance';
import { Dialog, Box, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, Snackbar, Alert, CircularProgress, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { DataGrid } from '@mui/x-data-grid';

import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import HistoryIcon from '@mui/icons-material/History';
import IconButton from '@mui/material/IconButton';

import CreatePurcaseOrder from './CreatePurchaseOrder';
import PurchaseOrderEdit from './PurchaseOrderEdit';
import PurchaseOrderView from './PurchaseOrderView';
import CreateRevisionPO from './CreateRevisionPO';
import HistoryPORevisions from './HistoryPurchaseOrder';
import NotAuthorized from '../../../Components/ProtectedRoute/NotAuthorized';

const getPathFromUrl = (url) => {
  try {
    const { pathname } = new URL(url);
    return pathname;
  } catch (e) {
    console.error("Eroare la extragerea URL-ului: ", e);
    return url;
  }
}

const PurcahseOrderList = ({ canCreate, canEdit }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [systemCurrencyCode, setSystemCurrencyCode] = useState('');

  //pentru fereastra pop-up in cazul respingerii aprobarii
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reason, setReason] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  //pentru afisare mesaje de tip snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  //functie de formatare suma
  const formatNumber = (value, locale = 'ro-RO') => {
    if (value === null || value === undefined || value === '') return '';

    const n = Number(value);
    if (Number.isNaN(n)) return '';

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const showMessage = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  }

  //functie pentru formatarea datei in format romanesc cu puncte
  const formatDateToRomanian = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  //fetch pentru moneda de sistem
  useEffect(() => {
    const fetchSystemCurrency = async () => {
      try {
        const response = await axiosInstance.get('/system-currency/1/');
        const currencyId = response.data?.currency || response.data?.id;
        
        if (currencyId) {
          try {
            const currencyResponse = await axiosInstance.get(`/currencies/${currencyId}/`);
            const currencyCode = currencyResponse.data?.code || currencyResponse.data?.name || '';
            setSystemCurrencyCode(currencyCode);
          } catch (err) {
            console.error("Eroare la preluarea detaliilor monedei: ", err);
            setSystemCurrencyCode('');
          }
        } else {
          setSystemCurrencyCode('');
        }
      } catch (error) {
        console.error("Eroare la preluarea monedei de sistem: ", error);
        setSystemCurrencyCode('');
      }
    };
    
    fetchSystemCurrency();
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axiosInstance.get("/profile/");
        setUserRole(response.data.role?.name || null);
      } catch (error) {
        console.error("Eroare la preluarea profilului utilizatorului: ", error);
      }
    };
    fetchUserProfile()
  }, []);

  //fetch pentru Purchase Order
  useEffect(() => {
    // if (!suppliers.length || !projectCode.length || !deliveryAddress.length) return;

    const fetchPurchaseOrder = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/purchase-order/");
        const storedData = response.data.sort((a, b) => b.id - a.id);
        setRows(storedData);
      } catch (error) {
        console.error("Eroare la preluarea PO:", error);
        showMessage("Error loading purchase orders!", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchPurchaseOrder();
  }, []);

  const getStatusClass = (status) => {
    switch (status) {
      case 'RECEIVED':
        return 'po-status-badge po-status-received';
      case 'DRAFT':
        return 'po-status-badge po-status-draft';
      case 'ORDERED':
        return 'po-status-badge po-status-ordered';
      case 'CANCELED':
        return 'po-status-badge po-status-canceled';
      case 'WAITING APPROVAL':
        return 'po-status-badge po-status-wappr';
      case 'APPROVED':
        return 'po-status-badge po-status-approved';
      case 'REJECTED':
        return 'po-status-badge po-status-rejected';
      case 'PARTIALLY RECEIVED':
        return 'po-status-badge po-status-partially';
      case 'REVISED':
        return 'po-status-badge po-status-revised';
      default:
        return 'po-status-badge';
    }
  };

  //handler pentru aprobarea comenzii
  const handleApprove = async (comandaId) => {
    try {
      const response = await axiosInstance.post(`/purchase-order/${comandaId}/comanda_aprobata/`);
      console.log("Approved:", response.data);
      
      setRows(prevRows => 
        prevRows.map(row => 
          row.id === comandaId 
            ? { 
                ...row, 
                approval: true,
                status: response.data.status || 'APPROVED',
              } 
            : row
        )
      );
      
      showMessage("The purchase order has been successfully approved!", "success");
    } catch (error) {
      console.error("Approval failed:", error);
      showMessage("Error while approving the purchase order!", "error");
    }
  };

  //handler pentru respingerea comenzii
  const handleReject = async (comandaId, reason) => {
    try {
      const response = await axiosInstance.post(`/purchase-order/${comandaId}/comanda_respinsa/`,
        { comment: reason }
      );
      console.log("Rejected:", response.data);
      
      setRows(prevRows => 
        prevRows.map(row => 
          row.id === comandaId 
            ? { 
                ...row, 
                approval: false,
                status: response.data.status || 'REJECTED',
                comment: response.data.comment || reason,
              } 
            : row
        )
      );
      
      showMessage("The purchase order has been rejected!", "info");
    } catch (error) {
      console.error("Approval failed:", error);
      showMessage("Error while rejecting the purchase order!", "error");
    }
  };

  //deschidere fereastra pop-up
  const handleOpenPopup = (orderId, orderNumber) => {
    setSelectedOrder({ id: orderId, number: orderNumber });
    setSelectedId(orderId)
    setOpen(true);
  }

  //inchide fereastra pop-up
  const handleClosePopup = (id) => {
    setOpen(false);
    setReason("");
  }

  //la click pe confirmare refuzare
  const handleConfirmReject = () => {
    handleReject(selectedId, reason);
    handleClosePopup();
  }

  //creare revizie
  const handleCreateRevisionClick = useCallback((row) => {
    navigate(`/purchase-order/create-revision/${row.id}`);
  }, [navigate]);

  //vizualizare istoric revizii
  const handleHistoryClick = useCallback((row) => {
    navigate(`/purchase-order/revision-history/${row.id}`);
  }, [navigate]);

  const columns = [
    {
      field: "user_full_name",
      headerName: "Issuer",
      minWidth: 100,
      flex: 1,
      headerClassName: "bold-header",
      renderCell: (params) => {
        const getColor = (name) => {
          if (!name || name === 'N/A') return '#ccc';

          let hash = 0;
          for(let i=0; i<name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
          }

          const hue = Math.abs(hash) % 360;
          return `hsl(${hue}, 70%, 50%)`;
        };

        const userName = params.value || 'N/A';
        const color = getColor(userName);

        return(
          <div className='user-cell'>
            <span
              className='user-dot'
              style = {{backgroundColor: color}}
            />
            {userName}
          </div>
        )
      }
    },
    { field: "order_number", headerName: "Purchase order", minWidth: 100, flex: 1, headerClassName: "bold-header" },
    { field: "supplier_name", headerName: "Supplier", minWidth: 190, flex: 1, headerClassName: "bold-header" },
    { field: "project_code_name", headerName: "Project code", minWidth: 150, flex: 1, headerClassName: "bold-header" },
    { field: "subproject_code_name", headerName: "Sub-project code", minWidth: 150, flex: 1, headerClassName: "bold-header" },
    { field: "delivery_address_name", headerName: "Delivery Address", minWidth: 300, flex: 1, headerClassName: "bold-header" },
    {
      field: "payment_status",
      headerName: "Payment Status",
      minWidth: 170,
      flex: 1,
      headerClassName: "bold-header",
      renderCell: (params) => {
        let statusClass = "";

        switch(params.value) {
          case "Fully Paid":
            statusClass = "payment-status-fully-paid";
            break;
          case "Uninvoiced": 
            statusClass = "payment-status-uninvoiced";
            break;
          case "Partially Paid":
            statusClass = "payment-status-partially-paid";
            break;
          case "Partially Invoiced":
            statusClass = "payment-status-partially-invoiced";
            break;
          case "Unpaid":
          default:
            statusClass = "payment-status-unpaid";
            break;
        }

        return (
          <div className={`po-status-badge ${statusClass}`}>
            {params.value}
          </div>
        );
      },
    },
    {
      field: "total",
      headerName: "Total",
      minWidth: 120,
      flex: 1,
      headerClassName: "bold-header",
      renderCell: (params) => {
        const currency = params.row.currency_code || "";
        return (
          <span>
            {formatNumber(params.value)} {currency}
          </span>
        );
      }
    },
    {
      field: "total_system_currency",
      headerName: `Total System (${systemCurrencyCode})`,
      minWidth: 140,
      flex: 1, 
      headerClassName: "bold-header",
      renderCell: (params) => {
        const value = params.value;
        const displayValue = (value !== null && value !== undefined) ? value : 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {formatNumber(displayValue)} {systemCurrencyCode}
          </Box>
        );
      }
    },
    {
      field: "arrival_deadline",
      headerName: "Expected Arrival",
      minWidth: 100,
      headerClassName: "bold-header",
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {formatDateToRomanian(params.value)}
        </Box>
      ),
    },
    {
      field: "status", 
      headerName: "Status", 
      minWidth: 180, 
      headerClassName: "bold-header",
      renderCell: (params) => {
        const status = params.value;
        const comment = params.row.comment;

        const showTooltip = status === 'REJECTED' && comment;
        return (
          <Tooltip
            title={showTooltip ? comment : ""}
            placement="top"
            arrow
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                cursor: showTooltip ? 'help' : 'default',
              }}
            >
              <span className={getStatusClass(status)}>
                {status.charAt(0) + status.slice(1).toLowerCase()}
              </span>
            </Box>
          </Tooltip>
        )
      },
    },
    ...(canEdit ? [{
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 125,
      headerClassName: "bold-header",
      headerAlign: "center",
      renderCell: (params) => (
        <div className='action-buttons-container'>
          <Button
            variant="text"
            title='View Purchase Order'
            startIcon={<VisibilityIcon />}
            className='action-button-view'
            onClick={() => {
              const { id, status } = params.row;
              if (status === "DRAFT") {
                navigate(`/purchase-order/draft/${id}`);
              } else if(status === "WAITING APPROVAL") {
                navigate(`/purchase-order/waiting-approval/${id}`);
              } else if(status === "APPROVED") {
                navigate(`/purchase-order/approved/${id}`);
              } else if(status === "ORDERED") {
                navigate(`/purchase-order/ordered/${id}`);
              } else if(status === "REJECTED") {
                navigate(`/purchase-order/rejected/${id}`);
              } else if(status === "RECEIVED") {
                navigate(`/purchase-order/received/${id}`);
              } else if(status === "PARTIALLY RECEIVED") {
                navigate(`/purchase-order/partially-received/${id}`);
              } else if(status === "CANCELED") {
                navigate(`/purchase-order/canceled/${id}`);
              } else if(status === "REVISED") {
                navigate(`/purchase-order/revised/${id}`);
              }
            }}
          />
          <Button
            variant="text"
            startIcon={<LibraryAddIcon />}
            title='Create revision'
            className='action-button-revision'
            onClick={() => handleCreateRevisionClick(params.row)}
          />

          <Button 
            variant="text"
            startIcon={<HistoryIcon />}
            title='View History'
            className='action-button-history'
            onClick={() => handleHistoryClick(params.row)}
          />
        </div>
      ),
    }] : []),
    ...(userRole === "RAMONA_CRACIUN" || userRole === "PAUL_NITU" ? [{
      field: "approval",
      headerName: "Approval?",
      minWidth: 150,
      flex: 1,
      headerClassName: "bold-header",
      headerAlign: "center",
      renderCell: (params) => {
        const approval = params.row.approval;
        const status = params.row.status;

        const isActive = status === "ORDERED" || status === "RECEIVED" || status === "WAITING APPROVAL" || status === "APPROVED" || status === "REJECTED" || status === "PARTIALLY RECEIVED";

        const handleApproveClick = () => {
          if (!isActive) {
            showMessage("This purchase order does not require approval!", "warning");
            return;
          };

          if (approval === true) {
            showMessage("This purchase order has already been approved!", "info");
          } else if (approval === false) {
            showMessage("This purchase order has already been rejected!", "info");
          } else {
            handleApprove(params.row.id);
          }
        };

        const handleRejectClick = () => {
          if (!isActive) {
            showMessage("This purchase order does not require approval!", "warning");
            return;
          }

          if (approval === true) {
            showMessage("This purchase order has already been approved!", "info");
          } else if (approval === false) {
            showMessage("This purchase order has already been rejected!", "info");
          } else {
            handleOpenPopup(params.row.id, params.row.order_number);
          }
        };

        let approveButtonClass = 'approval-button';
        let rejectButtonClass = 'approval-button';
        
        if (approval === true) {
          approveButtonClass += ' approval-button-approved';
        }
        if (approval === false) {
          rejectButtonClass += ' approval-button-rejected';
        }
        if (!isActive) {
          approveButtonClass += ' approval-button-disabled';
          rejectButtonClass += ' approval-button-disabled';
        }

        return (
          <div className='approval-buttons-container'>
            <IconButton
              color='success'
              className={approveButtonClass}
              onClick={handleApproveClick}
            >
              <CheckCircleOutlineIcon />
            </IconButton>

            <IconButton
              color='error'
              className={rejectButtonClass}
              onClick={handleRejectClick}
            >
              <CancelOutlinedIcon />
            </IconButton>
          </div>
        );
      }
    }] : []),
  ];

  return (
    <>
      <div className='po-page'>
        <div className='header-container'>
          <h2>PURCHASE ORDER</h2>

          <div className='po-actions'>
            {canCreate && (<Button
              className='create-po-button'
              variant='contained'
              color='primary'
              startIcon={<AddIcon />}
              onClick={() => navigate("/purchase-order/create")}
            >
              <span>Create Purchase Order</span>
            </Button>
            )}
          </div>
        </div>

        <Box className="po-box-subcontainer po-page po-datagrid">
          <div className='po-datagrid-container full-width-height custom-po-datagrid'>
            {loading ? (
              <div className='loading-overlay'>
                <CircularProgress size={60} thickness={4} className='loading-spinner' />
                <Typography variant="h6" className='loading-text'>
                  Loading data...
                </Typography>
              </div>
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                pageSize={5}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "id", sort: "desc" }],
                  },
                  pagination: {
                    paginationModel: { pageSize: 12 },
                  },
                }}
                disableRowSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    display: "flex",
                    alignItems: "center",
                    paddingTop: "3px",
                    paddingBottom: "3px",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    whiteSpace: "normal",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "transparent !important",
                  },
                }}
                pageSizeOptions={[10, 12, 20, 25]}
              />
            )}
          </div>
        </Box>

        <Dialog open={open} onClose={handleClosePopup} fullWidth maxWidth="md">
          <DialogTitle className='dialog-title'><b>Decline the Purchase Order {selectedOrder ? `#${selectedOrder.number}` : ""}</b></DialogTitle>
          <DialogContent>
            <TextField
              className='dialog-textfield'
              label="Please enter the reason why the purchase order is rejected"
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePopup}>Cancel</Button>
            <Button color='error' variant='contained' onClick={handleConfirmReject}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </>
  )
}

const PurchaseOrder = () => {
  const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
  const permissions = storedPermissions?.pages || [];

  const hasPermission = (path, checkPermission) => {
    const pagePermission = permissions.some((page) => getPathFromUrl(page.url) === path && checkPermission(page));
    return pagePermission;
  };

  const canCreate = hasPermission('/purchase-order', (page) => page.can_create);
  const canEdit = hasPermission('/purchase-order', (page) => page.can_edit);

  return (
    <Routes>
      <Route path="/" element={<PurcahseOrderList canCreate={canCreate} canEdit={canEdit} />} />
      <Route path="create" element={canCreate ? <CreatePurcaseOrder /> : <NotAuthorized />} />
      <Route path="draft/:id" element={canEdit ? <PurchaseOrderEdit /> : <NotAuthorized />} />
      <Route path="waiting-approval/:id" element={canEdit ? <PurchaseOrderView/>: <NotAuthorized />} />
      <Route path="approved/:id" element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path="rejected/:id" element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path="ordered/:id" element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path="received/:id" element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path="partially-received/:id" element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path="canceled/:id" element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path="edit/:id" element={canEdit ? <PurchaseOrderEdit /> : <NotAuthorized />} />
      <Route path="create-revision/:id" element={canEdit ? <CreateRevisionPO /> : <NotAuthorized />} />
      <Route path='revised/:id' element={canEdit ? <PurchaseOrderView /> : <NotAuthorized />} />
      <Route path='revision-history/:id' element={canEdit ? <HistoryPORevisions /> : <NotAuthorized />} />
    </Routes>
  )
}

export default PurchaseOrder;